// The menu preloads the resources (fields, questions, breakouts, calendar invites) of the layouts it links to, so
// that swapping to one of them without a full page load finds what its modules need. Those resources belong to the
// layout's EVENT, not to the layout: the server answers with the same data for every layout of one event
// (CmsEditorService.GetContentViewResources). So one request per event is enough.
//
// Returns the requests to make: one group per known event, and one per layout whose event is not known (those are
// asked for one by one, as before).
//   layoutCodes    - the layouts to cover
//   eventIdByCode  - { layoutCode: eventId } for the layouts whose event is known
export const groupLinkedLayoutsByEvent = (layoutCodes, eventIdByCode = {}) => {
  const groups = new Map();
  (layoutCodes || []).forEach(code => {
    const eventId = eventIdByCode?.[code];
    const known = eventId !== undefined && eventId !== null;
    const key = known ? `event:${eventId}` : `layout:${code}`;
    if (!groups.has(key)) groups.set(key, { key, eventId: known ? eventId : null, codes: [] });
    groups.get(key).codes.push(code);
  });
  return [...groups.values()];
};

// Makes sure the resources of every linked layout are loaded, with as few requests as possible. Safe to call
// several times and at the same time (two menus on one page): a layout or an event is never asked for twice.
//   layoutCodes        - the layouts the menu links to
//   eventIdByCode      - { layoutCode: eventId } where known
//   currentLayoutCode  - the page itself; it loads its own resources
//   loadedCodes        - Set of layout codes already covered (kept by the caller between calls)
//   loadedEvents       - Map of 'event:<id>' -> Set of the layout codes it covers (kept by the caller between calls)
//   fetchResources     - async layoutCode => the resources of that layout's event (tried twice before giving up,
//                        unless the server answered 4xx)
//   applyResources     - (resources, group) => void; merges them into the store
//   inAction           - runs a state change (MobX runInAction in the store)
//   onError            - (group, error) => void
// The server's 4xx (the linked layout does not exist, is closed or not published - menus often still link to such
// layouts) is an answer, not a failure: it is neither retried nor asked again later in the same page view. Only a
// failed request (network, 5xx) is retried and released for a later call.
export const isAnswerThatWillNotChange = err => {
  const status = err?.response?.status;
  return typeof status === 'number' && status >= 400 && status < 500;
};

export const preloadLinkedResources = async ({
  layoutCodes,
  eventIdByCode = {},
  currentLayoutCode = null,
  loadedCodes,
  loadedEvents,
  fetchResources,
  applyResources,
  inAction = fn => fn(),
  onError = () => {},
}) => {
  if (!Array.isArray(layoutCodes)) return;
  const toLoad = [...new Set(layoutCodes)].filter(code => !!code && code !== currentLayoutCode && !loadedCodes.has(code));
  if (toLoad.length === 0) return;

  // Reserve first, before anything is awaited, so a concurrent caller does not start the same requests.
  const groups = [];
  inAction(() => {
    toLoad.forEach(code => loadedCodes.add(code));
    groupLinkedLayoutsByEvent(toLoad, eventIdByCode).forEach(group => {
      if (group.eventId === null) return groups.push(group);
      const covered = loadedEvents.get(group.key);
      if (covered) {
        // An earlier call already asks for (or has) this event: these layouts ride along with it.
        group.codes.forEach(code => covered.add(code));
      } else {
        loadedEvents.set(group.key, new Set(group.codes));
        groups.push(group);
      }
    });
  });

  await Promise.all(
    groups.map(async group => {
      try {
        let data;
        try {
          data = await fetchResources(group.codes[0]);
        } catch (first) {
          if (isAnswerThatWillNotChange(first)) throw first;
          // One retry, as the page's own resource fetch does: with a single request per event there is no longer a
          // second, identical request to fall back on.
          data = await fetchResources(group.codes[0]);
        }
        inAction(() => applyResources(data, group));
      } catch (err) {
        onError(group, err);
        // A 4xx stays reserved: asking again during this page view would get the same answer.
        if (isAnswerThatWillNotChange(err)) return;
        // Let a later call try again - for every layout that was counting on this request.
        inAction(() => {
          const codes = group.eventId === null ? group.codes : [...(loadedEvents.get(group.key) || group.codes)];
          codes.forEach(code => loadedCodes.delete(code));
          if (group.eventId !== null) loadedEvents.delete(group.key);
        });
      }
    })
  );
};

// { layoutCode: eventId } from a list of layouts (the menu's "all layouts of the event" list).
export const eventIdsByLayoutCode = layouts => {
  const map = {};
  (Array.isArray(layouts) ? layouts : []).forEach(layout => {
    if (layout?.layoutCode && layout.eventId !== undefined && layout.eventId !== null) map[layout.layoutCode] = layout.eventId;
  });
  return map;
};
